import { observable, action, makeObservable } from 'mobx'
import { RootStore } from '.'
import { FileObject, UploadFile } from '../utils/models'
import { storageAPI } from '../utils/api'
import axios from 'axios'
import { pathString, CancelToken, cancelFunctions } from '../utils/helpers'
import PQueue from 'p-queue'

class FileStore {
  files: FileObject[] = []
  fetchingFiles: boolean = false
  path: string[] = []
  unfinishedUploads: UploadFile[] = []
  queuedUploads: UploadFile[] = []

  private rootStore: RootStore

  constructor(rootStore: RootStore) {
    //@ts-ignore
    makeObservable(this, {
      files: observable,
      fetchingFiles: observable,
      path: observable,
      unfinishedUploads: observable,
      queuedUploads: observable,
      changePath: action,
      fetch: action,
      updateFileUploadProgress: action,
      addPendingUpload: action,
      cancelPendingUpload: action,
      queueFiles: action,
      handleUpload: action
    })

    this.rootStore = rootStore
  }

  changePath = async (newPath: string) => {
    this.fetchingFiles = true
    const path = newPath.split('/').filter(Boolean)
    this.path = path
    this.fetch()
  }

  fetch = async (noLoader?: boolean) => {
    if (!noLoader) {
      this.fetchingFiles = true
    }

    await storageAPI.userInit()
    const files = await storageAPI.listFiles(this.path)
    this.files = files
    this.fetchingFiles = false
  }

  updateFileUploadProgress = (file: UploadFile) => {
    if (file.progress === 100) {
      this.unfinishedUploads = this.unfinishedUploads.filter(
        f => f.id !== file.id,
      )
      return
    }
    const matched = this.unfinishedUploads.find(f => f.id === file.id)
    if (matched) {
      matched.progress = file.progress
    }
  }

  addPendingUpload = (file: UploadFile) => {
    this.unfinishedUploads.push(file)
  }

  cancelPendingUpload = (file: UploadFile) => {
    this.unfinishedUploads = this.unfinishedUploads.filter(
      f => f.id !== file.id,
    )
  }

  queueFiles = async (pendingUploads: UploadFile[]) => {
    this.queuedUploads = pendingUploads

    const queue = new PQueue({ concurrency: 4 })

    for (const file of pendingUploads) {
      // const task = this.handleUpload(file)

      ;(async () => {
        await queue.add(() => this.handleUpload(file))
        this.queuedUploads = this.queuedUploads.filter(f => f.id !== file.id)
        console.log('Done: ', file.name)
      })()
    }
  }

  private uploadFileToS3 = async (presignedPostData: any, file: UploadFile) => {
    const formData = new FormData()
    Object.keys(presignedPostData.fields).forEach(key => {
      formData.append(key, presignedPostData.fields[key])
    })

    // Actual file has to be appended last.
    formData.append('file', file.src.file)

    const config = {
      cancelToken: new CancelToken(c => {
        cancelFunctions.set(file.id, c)
      }),
      onUploadProgress: ({ loaded, total }: any) => {
        var percentCompleted = Math.round((loaded * 100) / total)

        const updatedFile = {
          ...file,
          progress: percentCompleted,
        }

        this.updateFileUploadProgress(updatedFile)
      },
    }
    await axios.post(presignedPostData.url, formData, config)
  }

  handleUpload = async (file: UploadFile) => {
    console.warn('handling upload', file.name)
    const path = pathString(this.path)
    const presignedPostData = await storageAPI.requestSignature(file, path)
    try {
      file.uploadStarted = new Date()
      this.addPendingUpload(file)

      await this.uploadFileToS3(presignedPostData, file)

      this.fetch(true)
    } catch (e) {
      console.error('Upload Failed!', e.message)
    }
  }
}

export default FileStore
